import { FC } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import html2canvas from "html2canvas";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { formatDateTime } from "@app/shared/formatDateTime";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";
import { ZipCoverageMapSelections } from "@app/projects/zip-projects/maps/interfaces";

const EXPORT_CAPTURE_SELECTOR = ".zip-coverage-map__map-container";
const MAX_MEMBERSHIP_TYPE_LENGTH = 50;
const MAX_PROJECT_NAME_LENGTH = 75;

interface ZipCoverageMapExportProps {
  appliedSelections: ZipCoverageMapSelections;
  isLoading: boolean;
  membershipType: string | null;
  projectName: string;
  resultsAsOfDate: string | Date | null;
}

export const ZipCoverageMapExport: FC<ZipCoverageMapExportProps> = ({ appliedSelections, isLoading, membershipType, projectName, resultsAsOfDate }) => {
  const showMembershipType = !!membershipType;

  const getTitleBanner = (): HTMLDivElement => {
    const output = document.createElement("div");
    output.innerHTML =
      renderToStaticMarkup(
        <div className="title-div">
          <div className="text-container">
            <span className="title-text">
              Zip Coverage Map of {appliedSelections.specialtyGroupCode.name} for {appliedSelections.serviceArea.label}
              {appliedSelections.serviceArea.stateName ? `, ${appliedSelections.serviceArea.stateName}` : ""}
            </span>
            <div className="info-div">
              <span className="zip-coverage-map__info-text">
                <strong>QES Project Name:</strong> {truncateStringWithEllipsis(projectName, MAX_PROJECT_NAME_LENGTH)}
              </span>
              {showMembershipType && (
                <span className="zip-coverage-map__info-text">
                  <strong>Membership Type:</strong> {truncateStringWithEllipsis(membershipType, MAX_MEMBERSHIP_TYPE_LENGTH)}
                </span>
              )}
            </div>
            <div className="info-div">
              <span className="date-text">
                <strong>Map Created on:</strong>{" "}
                {new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              <span className="date-text">
                <strong>Results As Of:</strong> {resultsAsOfDate ? formatDateTime(resultsAsOfDate) : "Unknown Date"}
              </span>
            </div>
          </div>
          <img
            alt="Quest Analytics"
            className="title-image"
            id="logo"
            role="img"
            src={`${window.customUIBaseUrl}/images/QuestAnalyticsLogo-R.png`}
          />
        </div>
      );

    return output;
  }

  const getDetailsSectionSelector = (showDetails: boolean): string =>
    showDetails ? `.qes-zip-coverage-map-details` : `.qes-zip-coverage-map-details__applied-filters-container`;

  const handleExport = (showDetails: boolean): void => {
    const titleBanner = getTitleBanner();
    const areaLabel = appliedSelections.serviceArea.stateName
      ? `${appliedSelections.serviceArea.label} ${appliedSelections.serviceArea.stateName}`
      : appliedSelections.serviceArea.label;
    const exportNameRaw = `Zip Coverage Map ${appliedSelections.specialtyGroupCode.name} ${areaLabel}${showDetails ? " with Details" : ""}`;
    const exportName = exportNameRaw.replace(/\./g, "_");
    const exportDiv = document.querySelector(EXPORT_CAPTURE_SELECTOR) as HTMLElement | null;
    if (!exportDiv) {
      return;
    }

    const mapDiv = exportDiv.querySelector(".mapboxgl-map") as HTMLElement | null;
    const mapHeight = mapDiv?.offsetHeight ?? exportDiv.offsetHeight;

    html2canvas(exportDiv, {
      ignoreElements: (element) => element.classList.contains("mapboxgl-ctrl-group"),
      onclone: (clonedDoc) => {
        const clonedExportDiv = clonedDoc.querySelector(EXPORT_CAPTURE_SELECTOR) as HTMLElement | null;
        if (clonedExportDiv) {
          clonedExportDiv.classList.add("zip-coverage-map__map-export-canvas-container");
          clonedExportDiv.style.height = "auto";
          clonedExportDiv.style.alignSelf = "flex-start";
          const clonedMapDiv = clonedExportDiv.querySelector(".mapboxgl-map") as HTMLElement | null;
          if (clonedMapDiv) {
            clonedMapDiv.style.height = `${mapHeight}px`;
          }
          const detailsSection = clonedDoc.querySelector(getDetailsSectionSelector(showDetails));
          if (detailsSection) {
            clonedExportDiv.insertBefore(detailsSection, clonedExportDiv.firstChild);
          }
          clonedExportDiv.insertBefore(titleBanner, clonedExportDiv.firstChild);
        }
      },
      removeContainer: true,
      scale: 2,
    }).then(exportCanvas => {
      const link = document.createElement("a");
      link.download = exportName;
      link.href = exportCanvas.toDataURL("image/png");
      link.click();
    });
  };

  return (
    <div className="zip-coverage-map__export-button-container">
      <NewActionMenu
        alignRight
        buttonText="Export"
        testId="zip-coverage-map__export-dropdown"
        buttonClassNames="qes-button-primary"
        dropup={false}
        loading={isLoading}
      >
        <NewActionMenuItem
          display="Export map image"
          isSelected={false}
          key={1}
          onClick={() => handleExport(false)}
          testId="zip-coverage-map__export-dropdown-option1"
        />
        <NewActionMenuItem
          display="Export map image with details"
          isSelected={false}
          key={2}
          onClick={() => handleExport(true)}
          testId="zip-coverage-map__export-dropdown-option2"
        />
      </NewActionMenu>
    </div>
  );
};
